/* eslint-disable i18next/no-literal-string */
import {
  BriefcaseMedical20Regular as HospitalIcon,
  BriefcaseMedical24Regular as HospitalIcon24,
  BuildingBank20Regular as BankIcon,
  BuildingBank24Regular as BankIcon24,
  ContactCard20Regular as ContactCardIcon,
  ContactCard24Regular as ContactCardIcon24,
  FluentIconsProps,
  Mail20Regular as MailIcon,
  Mail24Regular as MailIcon24,
  Payment20Regular as PaymentCardIcon,
  Payment24Regular as PaymentCardIcon24,
  Phone20Regular as PhoneIcon,
  Phone24Regular as PhoneIcon24,
} from '@fluentui/react-icons';
import { isItmDeleteWithHashEnabled } from 'common/utilities/FlightSetting';
import { objectKeys } from 'common/utilities/TypeHelpers';
import { MSAEmailAddressData, MSATelephoneNumberData } from 'features/identity/addItem/ItemInput/MSAImportSuggestion.Helpers';
import {
  AgeGroup,
  BankAccountDetails,
  CreditCardNumber,
  CyberAgentMonitoringDataType,
  CyberData,
  DebitCardNumber,
  DriverLicense,
  FullAddress,
  FullName,
  GetIdVerificationApiResponse,
  NationalId,
  OneTimeCyberAgentScanStatusData,
  Passport,
  UnauthOneTimeCyberAgentScanStatusData,
  UnverifiedContactInfo,
  UserItpSummary,
} from 'mcsclient/models';
import { ApiCallResultStatus, ApiRequestStatus } from 'store/apiCallResultStatus';

import { Breach } from './breach.types';
import { AugmentedBreach } from './breachSelectors';
import { CreditSummary } from './creditSummary.types';
import { ISelectedUserOnboarding } from './identityMonitoringState';

export type { CyberAgentMonitoringDataType } from 'mcsclient/models'; // data types that live in CyberData
export type MonitoringDataType = CyberAgentMonitoringDataType;

/**
 * Map MonitoringDataType to the corresponding value type
 */
type MonitoringDataValueTypes = {
  TelephoneNumber: { telephone: string; isVerified: boolean };
  EmailAddress: { email: string; isVerified: boolean };
  MedicalID: string;
  DriverLicense: DriverLicense;
  Iban: string;
  OtherCard: string;
  Passport: Passport;
  NationalId: NationalId;
  BankAccount: BankAccountDetails;
  CreditCard: CreditCardNumber;
  DebitCard: DebitCardNumber;
  SSN: {
    // indicates whether SSN has been added
    isAdded: boolean;
    // indicates whether SSN has been verified
    isVerified: boolean;
  };
};

/**
 * Helper function for augmenting dataType field to the {@link values} array and the individual values within
 *
 * @param dataType
 * @param values
 * @param hashValues
 */
const augmentDataType = <T extends MonitoringDataType>(
  dataType: T,
  values?: MonitoringDataValueTypes[T][],
  hashValues?: MonitoringDataValueTypes[T][]
): MonitoringDataArray<T> | undefined =>
  values
    ? {
        dataType,
        values,
        deleteValues: hashValues && isItmDeleteWithHashEnabled() ? hashValues : values,
      }
    : undefined;

type MetadataType = {
  [T in MonitoringDataType]: {
    /**
     * Getter function for extracting the list of values (augmented with dataType) for this MonitoringDataType
     */
    getValues: (subscriberProfile: SubscriberProfile) => MonitoringDataArray<T> | undefined;
    icon: React.FC<FluentIconsProps>;
    icon24: React.FC<FluentIconsProps>;
    maxCount: number;
    /**
     * When these data types are listed, we need to treat
     * this order a sort order for presentation.
     */
    order: number;
  };
};

export type SubscriberProfile = {
  /**
   * Whether subscriber's SSN has been added to profile
   */
  isSSNAdded: boolean;
  /**
   * Whether subscriber's SSN has been verified successfully
   */
  isSSNVerified: boolean;
  /**
   * Additional monitored data
   */
  cyberData: CyberData;
  /**
   * Unverified telephones/emails
   */
  unverifiedContactInfo: UnverifiedContactInfo;

  // Personal info
  dob?: string;
  fullName?: FullName;
  fullAddress?: FullAddress;
  ageGroup?: AgeGroup;

  // MSA Import
  msaData: {
    emailAddresses: MSAEmailAddressData;
    telephoneNumbers: MSATelephoneNumberData;
  };
};

/**
 * Union of { dataType: dataType, value: MonitoringDataValueTypes[dataType] } for all dataType in T
 * (which is a discriminated union as a result)
 */
export type MonitoringDataValue<T extends MonitoringDataType = MonitoringDataType> = {
  [dataType in T]: { dataType: dataType; value: MonitoringDataValueTypes[dataType] };
}[T];

/**
 * Similar to {@link MonitoringDataValue} but contains values array instead of single value
 */
export type MonitoringDataArray<T extends MonitoringDataType = MonitoringDataType> = {
  [dataType in T]: {
    dataType: dataType;
    /**
     * List of values for this data type
     */
    values: MonitoringDataValueTypes[dataType][];
    /**
     * Values to be used in request when deleting info
     */
    deleteValues: MonitoringDataValueTypes[dataType][];
  };
}[T];

export const MonitoringDataTypeMetadata: MetadataType = {
  TelephoneNumber: {
    getValues: ({ cyberData, unverifiedContactInfo }) =>
      augmentDataType('TelephoneNumber', [
        // unverified telephones
        ...(unverifiedContactInfo.telephoneNumbers?.map((telephone) => ({ telephone, isVerified: false })) ?? []),
        // verified telephones
        ...(cyberData.telephoneNumbers?.map((telephone) => ({ telephone, isVerified: true })) ?? []),
      ]),
    icon: PhoneIcon,
    icon24: PhoneIcon24,
    maxCount: 10,
    order: 12,
  },
  EmailAddress: {
    getValues: ({ cyberData, unverifiedContactInfo }) =>
      augmentDataType('EmailAddress', [
        // unverified emails
        ...(unverifiedContactInfo.emailAddresses?.map((email) => ({ email, isVerified: false })) ?? []),
        // verified emails
        ...(cyberData.emailAddresses?.map((email) => ({ email, isVerified: true })) ?? []),
      ]),
    icon: MailIcon,
    icon24: MailIcon24,
    maxCount: 11,
    order: 1,
  },
  MedicalID: {
    getValues: ({ cyberData }) => augmentDataType('MedicalID', cyberData.medicalIds, cyberData.hashedMedicalIds),
    icon: HospitalIcon,
    icon24: HospitalIcon24,
    maxCount: 10,
    order: 6,
  },
  DriverLicense: {
    getValues: ({ cyberData }) =>
      augmentDataType(
        'DriverLicense',
        // cyberData.driverLicense is not an array: wrap it in an array for convenience
        cyberData.driverLicense ? [cyberData.driverLicense] : undefined,
        cyberData.hashedDriverLicense ? [cyberData.hashedDriverLicense] : undefined
      ),
    icon: ContactCardIcon,
    icon24: ContactCardIcon24,
    maxCount: 1,
    order: 5,
  },
  Iban: {
    getValues: ({ cyberData }) => augmentDataType('Iban', cyberData.ibans, cyberData.hashedIbans),
    icon: BankIcon,
    icon24: BankIcon24,
    maxCount: 10,
    order: 10,
  },
  OtherCard: {
    getValues: ({ cyberData }) => augmentDataType('OtherCard', cyberData.otherCards, cyberData.hashedOtherCards),
    icon: PaymentCardIcon,
    icon24: PaymentCardIcon24,
    maxCount: 10,
    order: 11,
  },
  Passport: {
    getValues: ({ cyberData }) => augmentDataType('Passport', cyberData.passports, cyberData.hashedPassports),
    icon: ContactCardIcon,
    icon24: ContactCardIcon24,
    maxCount: 5,
    order: 3,
  },
  NationalId: {
    getValues: ({ cyberData }) => augmentDataType('NationalId', cyberData.nationalIds, cyberData.hashedNationalIds),
    icon: ContactCardIcon,
    icon24: ContactCardIcon24,
    maxCount: 5,
    order: 4,
  },
  BankAccount: {
    getValues: ({ cyberData }) =>
      augmentDataType('BankAccount', cyberData.accountDetails?.bankAccounts, cyberData.accountDetails?.hashedBankAccounts),
    icon: BankIcon,
    icon24: BankIcon24,
    maxCount: 10,
    order: 9,
  },
  CreditCard: {
    getValues: ({ cyberData }) =>
      augmentDataType('CreditCard', cyberData.accountDetails?.creditCardNumbers, cyberData.accountDetails?.hashedCreditCardNumbers),
    icon: PaymentCardIcon,
    icon24: PaymentCardIcon24,
    maxCount: 5,
    order: 7,
  },
  DebitCard: {
    getValues: ({ cyberData }) =>
      augmentDataType('DebitCard', cyberData.accountDetails?.debitCardNumbers, cyberData.accountDetails?.hashedDebitCardNumbers),
    icon: PaymentCardIcon,
    icon24: PaymentCardIcon24,
    maxCount: 5,
    order: 8,
  },
  SSN: {
    getValues: ({ isSSNAdded, isSSNVerified }) =>
      augmentDataType('SSN', isSSNAdded ? [{ isAdded: isSSNAdded, isVerified: isSSNVerified }] : undefined),
    icon: ContactCardIcon,
    icon24: ContactCardIcon24,
    maxCount: 1,
    order: 2,
  },
};

export const MonitoringDataTypeNames: MonitoringDataType[] = objectKeys(MonitoringDataTypeMetadata);

export const isMonitoringDataType = (dataType: string): dataType is MonitoringDataType => dataType in MonitoringDataTypeMetadata;

export interface IOneTimeScan {
  statusData: OneTimeCyberAgentScanStatusData | undefined;
  breaches: Breach[];
  statusDataApiStatus: ApiCallResultStatus;
  emailCodeVerificationStatus: CodeVerificationStatus;
  scanAndGetBreachesApiStatus: ApiCallResultStatus;
  getExistingBreachesApiStatus: ApiCallResultStatus;
}

export interface IUnauthOneTimeScan {
  statusData: UnauthOneTimeCyberAgentScanStatusData | undefined;
  breaches: AugmentedBreach[];
  sessionId: string | undefined;
  statusDataApiStatus: ApiCallResultStatus;
  emailCodeVerificationStatus: CodeVerificationStatus;
  scanAndGetBreachesApiStatus: ApiCallResultStatus;
}

export interface IItpUserContext {
  /**
   * csid of ITP user
   * Note that family member who is not onboarded to ITP & never logged in may have undefined csid
   */
  csid: string | undefined;

  /**
   * userId of ITP user
   * Note that userId may be undefined for a family member in webview dialog since url contains only csid.
   */
  userId: string | undefined;

  /**
   * whether it's the current user or not
   */
  isCurrentUser: boolean;

  /**
   * User display name.
   */
  displayName?: string;

  /**
   * Onboarding context for the selected user, from IDSAPI endpoints
   */
  selectedUserOnboarding?: ISelectedUserOnboarding;
}

export enum CodeVerificationStatus {
  DEFAULT,
  GENERATING_CODE,
  GENERATION_ERROR,
  CODE_UNVERIFIED,
  VERIFYING,
  VALID,
  INVALID,
}

export enum SSNVerificationStatus {
  DEFAULT,
  PENDING_GET_QUESTIONS,
  PENDING_SUBMIT_ANSWERS,
  FAILED_GET_QUESTIONS,
  FAILED_SUBMIT_ANSWERS,
  SUCCESS_GET_QUESTIONS,
  SUCCESS_SUBMIT_ANSWERS,
}

export interface IFamilyMemberBreaches {
  breaches: Breach[];
  getBreachesApiStatus: ApiCallResultStatus;
}

export interface IFamilyMemberCreditSummary {
  creditSummary: CreditSummary | undefined;
  getCreditSummaryApiStatus: ApiCallResultStatus;
}

/**
 * Identity monitoring profile and cyber data for a member/subscriber
 */
export interface IFamilyMember
  extends Omit<UserItpSummary, 'itPOnboarded' /* deprecated */>,
    IFamilyMemberBreaches,
    IFamilyMemberCreditSummary {
  // subscriber complete profile
  getCompleteSubscriberProfileApiStatus: ApiCallResultStatus;
  subscriberProfile: SubscriberProfile;
  primaryEmailAddress: string | undefined;

  // delete subscription api status
  deleteSubscriptionApiStatus: ApiRequestStatus | undefined;

  // email code verification status
  emailCodeVerificationStatus: CodeVerificationStatus;

  // phone code verification status
  phoneCodeVerificationStatus: CodeVerificationStatus;

  // SSN verification
  ssnVerification: {
    requestId?: string;
    status: SSNVerificationStatus;
    questions?: GetIdVerificationApiResponse;
  };
}
